(() => {
  if (customElements.get('cursor-sparkles')) return;

  const registry = window.CursorSparklesRegistry || (window.CursorSparklesRegistry = {
    instances: [],
    active: null
  });
  const particleBehavior = {
    maximumParticles: 48,
    minimumEmissionInterval: 40,
    maximumTrailPoints: 2,
    offset: { minimum: -8, maximum: 8 },
    driftX: { minimum: -20, maximum: 20 },
    driftY: { minimum: -42, maximum: -16 },
    initialRotation: { minimum: 0, maximum: 180 },
    finalRotation: { minimum: 180, maximum: 540 },
    duration: { minimum: 480, maximum: 820 },
    expiryTolerance: 160
  };
  const settings = {
    density: { minimum: 1, maximum: 5, fallback: 2 },
    minSize: { minimum: 1, maximum: 20, fallback: 6 },
    maxSize: { minimum: 12, maximum: 24, fallback: 12 },
    minDistance: { minimum: 8, maximum: 40, fallback: 16 }
  };
  const svgNamespace = 'http://www.w3.org/2000/svg';
  const shapes = [
    'M12 0 14.9 9.1 24 12l-9.1 2.9L12 24l-2.9-9.1L0 12l9.1-2.9Z',
    'M10 0h4v10h10v4H14v10h-4V14H0v-4h10Z',
    'M12 0c1.3 7.4 4.6 10.7 12 12-7.4 1.3-10.7 4.6-12 12-1.3-7.4-4.6-10.7-12-12C7.4 10.7 10.7 7.4 12 0Z'
  ];

  class CursorSparkles extends HTMLElement {
    constructor() {
      super();
      this.isEffectConnected = false;
      this.isActive = false;
      this.isListening = false;
      this.animationFrame = null;
      this.pendingPoint = null;
      this.lastPoint = null;
      this.lastEmissionTime = 0;
      this.particles = [];
      this.particleExpirations = new Map();
      this.cleanupTimer = null;
      this.isCleaningExpiredParticles = false;
      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.handleMediaChange = this.handleMediaChange.bind(this);
      this.processPointer = this.processPointer.bind(this);
      this.finePointerQuery = window.matchMedia('(any-hover: hover) and (any-pointer: fine)');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    }

    connectedCallback() {
      if (this.isEffectConnected) return;

      this.isEffectConnected = true;
      this.config = this.getConfig();

      if (!this.config.isEnabled) return;

      this.addMediaListeners();
      registry.instances.push(this);

      if (!registry.active) {
        registry.active = this;
        this.activate();
      }
    }

    disconnectedCallback() {
      if (!this.isEffectConnected) return;

      this.isEffectConnected = false;
      this.removeMediaListeners();
      this.deactivate();
      registry.instances = registry.instances.filter((instance) => instance !== this);

      if (registry.active === this) {
        registry.active = null;
        const nextOwner = registry.instances.find((instance) => instance.isEffectConnected);

        if (nextOwner) {
          registry.active = nextOwner;
          nextOwner.activate();
        }
      }
    }

    getConfig() {
      const computedStyle = window.getComputedStyle(this);
      const minSize = this.parseSetting(this.dataset.minSize, settings.minSize);
      const maxSize = this.parseSetting(this.dataset.maxSize, settings.maxSize);
      const normalizedSizes = [minSize, maxSize].sort((first, second) => first - second);

      return {
        isStylesReady: this.isStylesReady(computedStyle),
        isEnabled: this.dataset.enabled !== 'false',
        sourceMode: this.dataset.sourceMode === 'files' ? 'files' : 'css',
        density: this.parseSetting(this.dataset.density, settings.density, false),
        minSize: normalizedSizes[0],
        maxSize: normalizedSizes[1],
        minDistance: this.parseSetting(this.dataset.minDistance, settings.minDistance),
        colors: [
          computedStyle.getPropertyValue('--cursor-sparkles-color-white').trim(),
          computedStyle.getPropertyValue('--cursor-sparkles-color-yellow').trim(),
          computedStyle.getPropertyValue('--cursor-sparkles-color-gold').trim()
        ].filter(Boolean),
        files: [
          this.dataset.sparkleFileOne,
          this.dataset.sparkleFileTwo,
          this.dataset.sparkleFileThree
        ].filter(Boolean)
      };
    }

    isStylesReady(computedStyle = window.getComputedStyle(this)) {
      return computedStyle.getPropertyValue('--cursor-sparkles-ready').trim() === '1';
    }

    parseSetting(value, bounds, integer = true) {
      const parsed = Number(value);

      if (!Number.isFinite(parsed)) return bounds.fallback;

      const normalized = integer ? Math.round(parsed) : parsed;
      return Math.min(bounds.maximum, Math.max(bounds.minimum, normalized));
    }

    addMediaListeners() {
      [this.finePointerQuery, this.reducedMotionQuery].forEach((query) => {
        if (query.addEventListener) {
          query.addEventListener('change', this.handleMediaChange);
        } else {
          query.addListener(this.handleMediaChange);
        }
      });
    }

    removeMediaListeners() {
      [this.finePointerQuery, this.reducedMotionQuery].forEach((query) => {
        if (query.removeEventListener) {
          query.removeEventListener('change', this.handleMediaChange);
        } else {
          query.removeListener(this.handleMediaChange);
        }
      });
    }

    handleMediaChange() {
      if (this === registry.active) {
        this.activate();
      }
    }

    activate() {
      if (!this.isEffectConnected || this !== registry.active) return;

      this.config = this.getConfig();
      const hasSources = this.config.sourceMode === 'files' ? this.config.files.length : this.config.colors.length;

      if (!this.config.isEnabled || !this.config.isStylesReady || !this.finePointerQuery.matches || this.reducedMotionQuery.matches || !hasSources) {
        this.deactivate();
        return;
      }

      if (this.isActive) return;

      this.isActive = true;
      document.addEventListener('pointermove', this.handlePointerMove, { passive: true });
      this.isListening = true;
    }

    deactivate() {
      if (this.isListening) {
        document.removeEventListener('pointermove', this.handlePointerMove);
        this.isListening = false;
      }

      this.isActive = false;
      this.pendingPoint = null;
      this.lastPoint = null;
      this.lastEmissionTime = 0;

      if (this.animationFrame !== null) {
        window.cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
      }

      this.clearParticles();
    }

    handlePointerMove(event) {
      if (event.pointerType !== 'mouse') return;

      this.pendingPoint = { x: event.clientX, y: event.clientY };

      if (this.animationFrame === null) {
        this.animationFrame = window.requestAnimationFrame(this.processPointer);
      }
    }

    processPointer() {
      this.animationFrame = null;

      if (!this.isActive || !this.pendingPoint) return;

      if (!this.isStylesReady()) {
        this.deactivate();
        return;
      }

      const point = this.pendingPoint;
      this.pendingPoint = null;

      if (!this.lastPoint) {
        this.lastPoint = point;
        return;
      }

      const deltaX = point.x - this.lastPoint.x;
      const deltaY = point.y - this.lastPoint.y;
      const distance = Math.hypot(deltaX, deltaY);

      if (distance < this.config.minDistance) return;

      const now = window.performance.now();
      if (now - this.lastEmissionTime < particleBehavior.minimumEmissionInterval) return;

      const trailPoints = Math.min(particleBehavior.maximumTrailPoints, Math.floor(distance / this.config.minDistance));

      for (let index = 1; index <= trailPoints; index += 1) {
        const progress = index / trailPoints;
        this.emit(point.x - deltaX + (deltaX * progress), point.y - deltaY + (deltaY * progress));
      }

      this.lastPoint = point;
      this.lastEmissionTime = now;
    }

    emit(x, y) {
      if (!this.isStylesReady()) return;

      for (let index = 0; index < this.getParticleCount(); index += 1) {
        if (this.particles.length >= particleBehavior.maximumParticles) {
          this.removeParticle(this.particles[0], false);
        }

        const size = this.random(this.config.minSize, this.config.maxSize);
        const particle = document.createElement('span');
        particle.className = 'cursor-sparkles-particle';
        particle.style.setProperty('--cursor-sparkles-size', `${size}px`);
        const offsetX = this.random(particleBehavior.offset.minimum, particleBehavior.offset.maximum);
        const offsetY = this.random(particleBehavior.offset.minimum, particleBehavior.offset.maximum);
        particle.style.setProperty('--cursor-sparkles-x', `${x + offsetX - (size / 2)}px`);
        particle.style.setProperty('--cursor-sparkles-y', `${y + offsetY - (size / 2)}px`);
        particle.style.setProperty('--cursor-sparkles-drift-x', `${this.random(particleBehavior.driftX.minimum, particleBehavior.driftX.maximum)}px`);
        particle.style.setProperty('--cursor-sparkles-drift-y', `${this.random(particleBehavior.driftY.minimum, particleBehavior.driftY.maximum)}px`);
        particle.style.setProperty('--cursor-sparkles-rotation', `${this.random(particleBehavior.initialRotation.minimum, particleBehavior.initialRotation.maximum)}deg`);
        particle.style.setProperty('--cursor-sparkles-rotation-end', `${this.random(particleBehavior.finalRotation.minimum, particleBehavior.finalRotation.maximum)}deg`);
        const duration = Math.round(this.random(particleBehavior.duration.minimum, particleBehavior.duration.maximum));
        particle.style.setProperty('--cursor-sparkles-duration', `${duration}ms`);
        if (this.config.sourceMode === 'css') {
          particle.style.setProperty('--cursor-sparkles-particle-color', this.pick(this.config.colors));
        }
        particle.appendChild(this.createShape());
        const removeParticle = () => this.removeParticle(particle);
        particle.addEventListener('animationend', removeParticle, { once: true });
        particle.addEventListener('animationcancel', removeParticle, { once: true });
        this.particles.push(particle);
        this.particleExpirations.set(particle, Date.now() + duration + particleBehavior.expiryTolerance);
        this.appendChild(particle);
      }

      this.scheduleParticleCleanup();
    }

    getParticleCount() {
      const guaranteedCount = Math.floor(this.config.density);
      const fractionalCount = this.config.density - guaranteedCount;
      return guaranteedCount + (Math.random() < fractionalCount ? 1 : 0);
    }

    createShape() {
      if (this.config.sourceMode === 'files') {
        const image = document.createElement('img');
        image.src = this.pick(this.config.files);
        image.alt = '';
        image.width = 24;
        image.height = 24;
        image.setAttribute('aria-hidden', 'true');
        image.draggable = false;
        return image;
      }

      const svg = document.createElementNS(svgNamespace, 'svg');
      const path = document.createElementNS(svgNamespace, 'path');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');
      path.setAttribute('d', this.pick(shapes));
      svg.appendChild(path);
      return svg;
    }

    clearParticles() {
      this.cancelParticleCleanup();
      this.particles.forEach((particle) => particle.remove());
      this.particles = [];
      this.particleExpirations.clear();
    }

    removeParticle(particle, reschedule = true) {
      if (!particle) return;

      particle.remove();
      this.particles = this.particles.filter((currentParticle) => currentParticle !== particle);
      this.particleExpirations.delete(particle);

      if (reschedule && !this.isCleaningExpiredParticles) {
        this.scheduleParticleCleanup();
      }
    }

    scheduleParticleCleanup() {
      this.cancelParticleCleanup();

      if (!this.particleExpirations.size) return;

      const nextExpiry = Math.min(...this.particleExpirations.values());
      this.cleanupTimer = window.setTimeout(() => {
        this.cleanupTimer = null;
        this.isCleaningExpiredParticles = true;
        const now = Date.now();

        this.particleExpirations.forEach((expiresAt, particle) => {
          if (expiresAt <= now) {
            this.removeParticle(particle);
          }
        });

        this.isCleaningExpiredParticles = false;
        this.scheduleParticleCleanup();
      }, Math.max(0, nextExpiry - Date.now()));
    }

    cancelParticleCleanup() {
      if (this.cleanupTimer !== null) {
        window.clearTimeout(this.cleanupTimer);
        this.cleanupTimer = null;
      }
    }

    pick(values) {
      return values[Math.floor(Math.random() * values.length)];
    }

    random(minimum, maximum) {
      return Math.random() * (maximum - minimum) + minimum;
    }
  }

  customElements.define('cursor-sparkles', CursorSparkles);
})();
